import { useMutation, useQueryClient } from '@tanstack/react-query';
import reviewLikesService from '~/services/reviewLikesService/review-likes.service';
import useUserContext from './useUserContext';
import { useState, useEffect, useCallback } from 'react';

interface UseReviewLikesProps {
  reviewId: string;
  initialLikeCount: number;
}

export const useReviewLikes = ({
  reviewId,
  initialLikeCount,
}: UseReviewLikesProps) => {
  const queryClient = useQueryClient();
  const { user, isAuthenticated, addLikedReview, removeLikedReview } =
    useUserContext();
  const [count, setCount] = useState(initialLikeCount);

  const isLiked = user?.likedReviewIdList?.includes(reviewId) ?? false;

  // Update count when initialLikeCount changes
  useEffect(() => {
    setCount(initialLikeCount);
  }, [initialLikeCount]);

  const toggleLikeMutation = useMutation({
    mutationFn: () => reviewLikesService.toggleLike(reviewId),
    onMutate: async () => {
      if (!isAuthenticated) return;

      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['review', reviewId] });

      // Snapshot the previous values
      const previousCount = count;

      // Optimistically update the UI
      const newCount = count + (isLiked ? -1 : 1);
      setCount(newCount);

      // Optimistically update the liked reviews in UserContext
      if (isLiked) {
        removeLikedReview(reviewId);
      } else {
        addLikedReview(reviewId);
      }

      // Return context with the snapshotted values
      return { previousCount, wasLiked: isLiked };
    },
    onError: (_, __, context) => {
      if (!context) return;

      // Restore previous count
      setCount(context.previousCount);

      // Restore previous liked state in UserContext
      if (context.wasLiked) {
        addLikedReview(reviewId);
      } else {
        removeLikedReview(reviewId);
      }
    },
    onSettled: () => {
      // Refetch after error or success to ensure data consistency
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
  });

  const handleToggleLike = useCallback(() => {
    if (!isAuthenticated) return;
    toggleLikeMutation.mutate();
  }, [isAuthenticated, toggleLikeMutation]);

  return {
    count,
    isLiked,
    toggleLike: handleToggleLike,
    isLoading: toggleLikeMutation.isPending,
    isAuthenticated,
  };
};
